import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './client';

/**
 * RFI surface (T-0035 / E04-F04). Engagement-scoped under
 * `/api/v1/engagements/:engagement_id/rfis`. The list endpoint returns
 * `RfiSummaryDto` items + a cursor. The detail endpoint surfaces the
 * full timeline + evidence links — fine for v1 even though the UI only
 * shows summary metadata.
 *
 * Status values on the wire come from the service-layer state machine:
 *   - open
 *   - response_received
 *   - closed
 *   - escalated
 *
 * The screen renders these as posture-style chips ("Sent", "Responded",
 * "Closed", "Overdue/Escalated"); the mapping is per-screen, not in the
 * API client.
 */

export const RfiSummarySchema = z.object({
  id: z.string().uuid(),
  rfi_number: z.string(),
  title: z.string(),
  recipient_kind: z.string(),
  status: z.string(),
  resolution_outcome: z.string().nullable(),
  opened_at: z.string(),
  responded_at: z.string().nullable(),
  closed_at: z.string().nullable(),
  created_by: z.string(),
});
export type RfiSummary = z.infer<typeof RfiSummarySchema>;

export const RfiListSchema = z.object({
  items: z.array(RfiSummarySchema),
  next_cursor: z.string().nullable(),
});
export type RfiList = z.infer<typeof RfiListSchema>;

export interface ListRfisOpts {
  status?: string;
  cursor?: string;
  limit?: number;
}

export function listRfis(engagementId: string, opts: ListRfisOpts = {}): Promise<RfiList> {
  return apiClient(`/api/v1/engagements/${encodeURIComponent(engagementId)}/rfis`, RfiListSchema, {
    query: opts as Record<string, string | number | boolean | undefined>,
  });
}

export function useRfiListQuery(engagementId: string | undefined, opts: ListRfisOpts = {}) {
  return useQuery({
    queryKey: ['rfis', engagementId, opts] as const,
    queryFn: () => listRfis(engagementId!, opts),
    enabled: Boolean(engagementId),
  });
}

// ===========================================================================
// RFI create (T-0071 backend — wired for the global create modal, Tier 1.2)
// ===========================================================================

export interface CreateRfiBody {
  title: string;
  detail: string;
  recipient_kind: 'vendor' | 'pmo';
  recipient_info?: Record<string, unknown> | null;
}

const RfiWriteResultSchema = z
  .object({ id: z.string().optional(), rfi_number: z.string().optional() })
  .passthrough();

export function useCreateRfiMutation(engagementId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ body, idempotencyKey }: { body: CreateRfiBody; idempotencyKey?: string }) =>
      apiClient(
        `/api/v1/engagements/${encodeURIComponent(engagementId!)}/rfis`,
        RfiWriteResultSchema,
        {
          method: 'POST',
          body,
          ...(idempotencyKey ? { idempotencyKey } : {}),
        },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['rfis', engagementId] });
    },
  });
}

// ===========================================================================
// RFI respond / close (Tier 2 — lifecycle actions on the RFI screen)
// ===========================================================================

export const RFI_RESOLUTION_OUTCOMES = [
  'resolved',
  'needs_further_discussion',
  'escalated',
] as const;
export type RfiResolutionOutcome = (typeof RFI_RESOLUTION_OUTCOMES)[number];

const RfiRespondResultSchema = z
  .object({
    id: z.string(),
    status: z.string(),
    elapsed_open_to_response_ms: z.number().optional(),
  })
  .passthrough();
const RfiCloseResultSchema = z.object({ id: z.string(), status: z.string() }).passthrough();

export function useRespondRfiMutation(engagementId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      rfiId,
      responseText,
      idempotencyKey,
    }: {
      rfiId: string;
      responseText: string;
      idempotencyKey?: string;
    }) =>
      apiClient(
        `/api/v1/engagements/${encodeURIComponent(engagementId!)}/rfis/${encodeURIComponent(rfiId)}/respond`,
        RfiRespondResultSchema,
        {
          method: 'POST',
          body: { response_text: responseText },
          ...(idempotencyKey ? { idempotencyKey } : {}),
        },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['rfis', engagementId] });
    },
  });
}

export function useCloseRfiMutation(engagementId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      rfiId,
      outcome,
      idempotencyKey,
    }: {
      rfiId: string;
      outcome: RfiResolutionOutcome;
      idempotencyKey?: string;
    }) =>
      apiClient(
        `/api/v1/engagements/${encodeURIComponent(engagementId!)}/rfis/${encodeURIComponent(rfiId)}/close`,
        RfiCloseResultSchema,
        {
          method: 'POST',
          body: { resolution_outcome: outcome },
          ...(idempotencyKey ? { idempotencyKey } : {}),
        },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['rfis', engagementId] });
    },
  });
}
